import {Module} from "../common/Module";
import {isPrivileged} from "../../config/Privileges";
import { ProjectAreaName, ProjectId} from "./Project";
import {Identifier} from "../common/Identifier";
import {SoftDeletable} from "../common/SoftDeletable";
import {Dateable, TimePeriodable} from "../common/TimePeriodable";
import {Nullable} from "../common/Nullable";
import {BudgetProp} from "../accounting/Budgetable";
import {Describable} from "../common/Describable";
import {Performanceable} from "../accounting/Performanceable";
import {ProjectCustomer} from "./ProjectCustomer";
import {ProjectResourceable, ProjectResourceContainer} from "./ProjectResourceable";
import {BillSettingsProp} from "../accounting/BillSettings";
import {OptionType} from "../../components/form/SearchableSelect";
import {ProjectStatusProp} from "./ProjectStatusProp";
import {Sensitive} from "../common/Sensitive";
import {Money} from "../accounting/Finance";

/**
 * Represents the module for managing project stages within the application.
 * Provides functionality to check user privileges related to project stage operations.
 */
export const ProjectStageModule: Module = {
    identifier: "projects_stages",
    isPrivileged: (authUser, privilege) => isPrivileged( authUser, ProjectAreaName, ProjectStageModule.identifier, privilege )
};

/**
 * returns the API path to fetch stages from as string
 *
 * @param projectId the identifier of the project to fetch stages for
 */
export const projectStageSelectRoute = ( projectId: number) => `projects/${projectId}/stages/select`;

/**
 * Represents the identifier for a specific stage in a project workflow.
 *
 * This interface is used to encapsulate and standardize how project stage identifiers
 * are referenced across the application. It ensures consistency when dealing with stages
 * of a project and their corresponding IDs.
 *
 * @property projectStageId - A unique identifier for a project stage, which is represented
 * as a number wrapped in the generic `Identifier` type. This ensures type safety and clarity
 * when working with stage identifiers.
 */
export interface ProjectStageId {
    projectStageId: Identifier<number>
}

/**
 * Represents a stage of a project, combining financial, resource, and timeline information
 * to facilitate project tracking, reporting, and decision-making.
 *
 * IMPORTANT:
 *      When stage is locked, all phases of the stage will also be locked!
 *
 * This interface aggregates multiple properties and facets related to a specific phase or
 * segment of a project. It is used to manage project-specific details, such as resources,
 * performance, financials, and associated constraints. Designed to work with highly
 * modular and extendable codebases.
 *
 * Implementations must adhere to the various aspects defined by the extended interfaces,
 * ensuring compatibility with budget, performance, and timeline tracking mechanisms.
 */
export interface ProjectStage extends BudgetProp,
    Describable,
    ProjectStageId,
    Omit< ProjectStageShort, 'projectStageId' >,
    ProjectId,
    SoftDeletable,
    TimePeriodable< Nullable<Dateable>, Nullable<Dateable> >,
    Performanceable,
    ProjectResourceable,
    ProjectResourceContainer,
    BillSettingsProp,
    ProjectStatusProp
{
    optionalRetailPrice: Sensitive<Money>
    optionalInternalRetailPrice: Sensitive<Money>
    optionalContractedRetailPrice: Sensitive<Money>
    commissions: Nullable< ProjectCustomer >
    isProjectAddendum: boolean
    hasProjectPhases: boolean
    externalWorkGroupId: Nullable< Identifier< number > >
    additionalCostPrice?: Sensitive<Money>
    retailPriceWithAdditionalCosts?: Sensitive<Money>
    optionalRetailPriceWithAdditionalCosts?: Sensitive<Money>
    optionalAdditionalCosts?: Sensitive<Money>
}

/**
 * Determines whether the given object represents a project stage.
 *
 * This function is used to identify if the provided object is of the type `ProjectStage`, based on its
 * `resourceTypeIdentifier` property. It is critical for ensuring type safety in workflows or operations
 * involving project-related resources.
 *
 * @param obj - The object to be validated. It must conform to the `ProjectResourceable` interface, which
 * includes a `resourceTypeIdentifier` property.
 * @return Returns `true` if the object represents a `ProjectStage`, otherwise returns `false`.
 */
export function isProjectStage( obj: ProjectResourceable ): obj is ProjectStage {
        return obj.resourceTypeIdentifier.rawValue === "ProjectStage"
}

/**
 * Represents a simplified view of a project's stage, used for managing and displaying stage information.
 * Serves as a compact version of a full project stage for lightweight interactions.
 */
export interface ProjectStageShort extends ProjectStageId {
    name: string
    /** automatically generated number, starts from 1 */
    number: number
    /**
     * is the stage active at the moment, non active stage cannot be billed ( also true for phases under it )
     * */
    isActive: boolean
}

/**
 * Represents an option type specifically tailored for project stages in the system.
 * Offers additional context regarding whether the option includes project phases.
 *
 * This interface is designed to extend the generic `OptionType` interface by adding properties
 * that are relevant to managing projects with distinct stages or phases.
 *
 * Typical use cases include defining configuration or metadata related to stages within a project
 * to enhance decision-making for stakeholders and streamline workflows for developers.
 *
 * Developers should note that this interface is flexible, with optional properties designed for
 * specific scenarios where project phases may or may not exist.
 *
 * @extends OptionType
 *
 * @property hasProjectPhases - Indicates whether this option supports or includes project phases.
 *                              Optional field; if undefined, no assumptions should be made about the presence of phases.
 */
 export interface ProjectStageOptionType extends OptionType {
    hasProjectPhases?: boolean
 }